import { useEffect, useState } from "react";
import { CheckCircle2, XCircle, ClipboardCheck, Clock, Drill, FileText, ChevronLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { shifts as shiftsApi, type ShiftRow } from "@/lib/api";
import { useRBAC } from "@/hooks/useRBAC";
import { safeFormatDate } from "@/lib/utils";

type TabStatus = "submitted" | "approved" | "rejected";

// Minimal shape we read from shift_data. The full LocalShiftLog type lives in
// the operator package — mirroring just the bits we show here keeps the dashboard
// bundle from pulling in the whole operator type tree.
interface ShiftData {
  shiftDetails?: {
    date?: string;
    shiftType?: string;
    operatorName?: string;
    rigId?: string;
    site?: string;
    location?: string;
  };
  drillHoles?: Array<{ holeNumber?: string; depth?: number; status?: string }>;
  delays?: Array<{ delayType?: string; timeSeconds?: number; notes?: string }>;
  consumables?: Array<{ type?: string; serialNumber?: string; metresDrilled?: number }>;
  notes?: string;
  supervisorNotes?: string | null;
  submittedAt?: string | null;
}

export default function Approvals() {
  const { can } = useRBAC();
  const [tab, setTab] = useState<TabStatus>("submitted");
  const [rows, setRows] = useState<ShiftRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<ShiftRow | null>(null);
  const [notes, setNotes] = useState("");
  const [acting, setActing] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const all = await shiftsApi.list({ limit: 300 });
      const filtered = all
        .filter((r) => r.status === tab)
        .sort((a, b) => (b.submitted_at ?? b.created_at).localeCompare(a.submitted_at ?? a.created_at));
      setRows(filtered);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // Clear any detail selection when switching tabs — stale state was confusing
    setSelected(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  async function act(newStatus: "approved" | "rejected") {
    if (!selected) return;
    setActing(true);
    try {
      const data = (selected.shift_data ?? {}) as ShiftData;
      const reviewedAt = new Date().toISOString();
      const updated: ShiftData = {
        ...data,
        supervisorNotes: notes || data.supervisorNotes || null,
      };
      await shiftsApi.upsert({
        id: selected.id,
        app_slug: "operator",
        shift_data: { ...updated, status: newStatus, reviewedAt } as unknown,
        status: newStatus,
        submitted_at: selected.submitted_at,
      });
      setSelected(null);
      setNotes("");
      await load();
    } finally {
      setActing(false);
    }
  }

  if (!can("approve:shifts")) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            You don't have permission to approve shifts.
          </CardContent>
        </Card>
      </div>
    );
  }

  // Detail view — after picking a shift from the list, show its summary + action buttons
  if (selected) {
    const d = (selected.shift_data ?? {}) as ShiftData;
    const sd = d.shiftDetails ?? {};
    const holes = d.drillHoles ?? [];
    const delays = d.delays ?? [];
    const consumables = d.consumables ?? [];
    const totalMetres = holes.reduce((s, h) => s + (h.depth ?? 0), 0);
    const totalDelayMins = Math.round(delays.reduce((s, x) => s + (x.timeSeconds ?? 0), 0) / 60);

    return (
      <div className="max-w-4xl mx-auto p-4 md:p-6 space-y-4">
        <Button variant="ghost" size="sm" onClick={() => { setSelected(null); setNotes(""); }}>
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back to list
        </Button>

        <div className="flex items-center justify-between flex-wrap gap-2">
          <h1 className="text-2xl font-bold text-foreground">
            {sd.operatorName ?? "Unknown"} — {safeFormatDate(sd.date, "EEE d MMM yyyy", "no date")}
          </h1>
          <Badge variant="secondary" className="capitalize">{selected.status}</Badge>
        </div>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Shift details</CardTitle></CardHeader>
          <CardContent className="space-y-1 text-sm">
            <div><span className="text-muted-foreground">Rig:</span> {sd.rigId ?? "—"} ({sd.shiftType ?? "—"} shift)</div>
            <div><span className="text-muted-foreground">Site / Location:</span> {sd.site ?? "—"} / {sd.location ?? "—"}</div>
            {d.submittedAt && (
              <div><span className="text-muted-foreground">Submitted:</span> {safeFormatDate(d.submittedAt, "d MMM yyyy, HH:mm")}</div>
            )}
            {d.notes && (
              <div><span className="text-muted-foreground">Operator notes:</span> {d.notes}</div>
            )}
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Drill className="h-4 w-4" /> Holes</CardTitle></CardHeader>
            <CardContent className="text-sm">
              <div className="text-2xl font-bold">{holes.length}</div>
              <div className="text-muted-foreground">{totalMetres.toFixed(1)}m total</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Clock className="h-4 w-4" /> Delays</CardTitle></CardHeader>
            <CardContent className="text-sm">
              <div className="text-2xl font-bold">{delays.length}</div>
              <div className="text-muted-foreground">{totalDelayMins} min total</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><FileText className="h-4 w-4" /> Consumables</CardTitle></CardHeader>
            <CardContent className="text-sm">
              <div className="text-2xl font-bold">{consumables.length}</div>
              <div className="text-muted-foreground">items logged</div>
            </CardContent>
          </Card>
        </div>

        {holes.length > 0 && (
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Drill holes</CardTitle></CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-muted-foreground">
                    <th className="py-2 px-4 font-medium">Hole</th>
                    <th className="py-2 px-4 font-medium">Depth</th>
                    <th className="py-2 px-4 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {holes.map((h, i) => (
                    <tr key={i} className="border-b border-border/50 last:border-0">
                      <td className="py-2 px-4 font-medium">{h.holeNumber ?? "—"}</td>
                      <td className="py-2 px-4">{h.depth?.toFixed?.(1) ?? "—"}m</td>
                      <td className="py-2 px-4 capitalize">{h.status ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}

        {delays.length > 0 && (
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Delays</CardTitle></CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-muted-foreground">
                    <th className="py-2 px-4 font-medium">Type</th>
                    <th className="py-2 px-4 font-medium">Duration</th>
                    <th className="py-2 px-4 font-medium">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {delays.map((x, i) => {
                    const mins = Math.round((x.timeSeconds ?? 0) / 60);
                    const h = Math.floor(mins / 60), m = mins % 60;
                    return (
                      <tr key={i} className="border-b border-border/50 last:border-0">
                        <td className="py-2 px-4 capitalize">{(x.delayType ?? "").replace(/-/g, " ")}</td>
                        <td className="py-2 px-4">{h > 0 ? `${h}h ${m}m` : `${m}m`}</td>
                        <td className="py-2 px-4 text-muted-foreground">{x.notes ?? ""}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}

        {selected.status === "submitted" && (
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Supervisor notes</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional — anything you want the operator to see."
                rows={3}
              />
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => void act("rejected")} disabled={acting}>
                  <XCircle className="h-4 w-4 mr-1.5" /> Reject
                </Button>
                <Button onClick={() => void act("approved")} disabled={acting}>
                  <CheckCircle2 className="h-4 w-4 mr-1.5" /> Approve
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {selected.status !== "submitted" && d.supervisorNotes && (
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Supervisor notes</CardTitle></CardHeader>
            <CardContent className="text-sm">{d.supervisorNotes}</CardContent>
          </Card>
        )}
      </div>
    );
  }

  // List view
  return (
    <div className="max-w-4xl mx-auto p-4 md:p-6 space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
          <ClipboardCheck className="h-6 w-6" /> Approvals
        </h1>
        <p className="text-muted-foreground text-sm">Review and approve shifts submitted from the operator app.</p>
      </div>

      <div className="flex gap-1 border-b border-border">
        {(["submitted", "approved", "rejected"] as TabStatus[]).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium border-b-2 capitalize transition-colors ${
              tab === t ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {t === "submitted" ? "Pending" : t}
          </button>
        ))}
      </div>

      {loading ? (
        <Card><CardContent className="py-10 text-center text-muted-foreground">Loading…</CardContent></Card>
      ) : rows.length === 0 ? (
        <Card><CardContent className="py-10 text-center text-muted-foreground">No {tab === "submitted" ? "pending" : tab} shifts.</CardContent></Card>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => {
            const d = (r.shift_data ?? {}) as ShiftData;
            const sd = d.shiftDetails ?? {};
            const totalMetres = (d.drillHoles ?? []).reduce((s, h) => s + (h.depth ?? 0), 0);
            return (
              <Card key={r.id} className="cursor-pointer hover:border-primary/50 transition-colors" onClick={() => setSelected(r)}>
                <CardContent className="p-4 flex items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">
                      {sd.operatorName ?? "Unknown operator"} — {sd.rigId ?? "—"}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {safeFormatDate(sd.date, "EEE d MMM yyyy", "no date")} · {sd.site ?? "—"} · {sd.shiftType ?? "—"} shift
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-medium">{(d.drillHoles ?? []).length} holes</div>
                    <div className="text-xs text-muted-foreground">{totalMetres.toFixed(1)}m</div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
